import { useCallback, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, 
  Edit2, 
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultScenario = {
  category: 'spouse',
  title: '',
  description: '',
  goal: '',
  difficulty_levels: {
    1: { tension: 'low', pushback: 'minimal' },
    2: { tension: 'moderate', pushback: 'some resistance' },
    3: { tension: 'high', pushback: 'defensive responses' }
  },
  system_prompt: '',
  opening_line: '',
  success_criteria: {
    uses_i_statements: true,
    proposes_solution: true,
    acknowledges_feelings: true,
    avoids_blame: true
  },
  tags: [],
  is_active: true
};

export default function Admin() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [formData, setFormData] = useState(defaultScenario);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const scenariosRes = await axios.get(`${API}/admin/scenarios`, { withCredentials: true });
      setScenarios(scenariosRes.data.items);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Scenario management could not be loaded');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingScenario(null);
    setFormData(defaultScenario);
    setDialogOpen(true);
  };

  const openEditDialog = (scenario) => {
    setEditingScenario(scenario);
    setFormData({
      ...scenario,
      tags: scenario.tags || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.goal) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingScenario) {
        await axios.put(`${API}/admin/scenarios/${editingScenario.id}`, formData, { withCredentials: true });
        toast.success('Scenario updated');
      } else {
        await axios.post(`${API}/admin/scenarios`, formData, { withCredentials: true });
        toast.success('Scenario created');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save scenario');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scenario?')) return;
    
    try {
      await axios.delete(`${API}/admin/scenarios/${id}`, { withCredentials: true });
      toast.success('Scenario deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete scenario');
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center" data-testid="admin-scenarios-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D7D1C7] border-t-[#2C5545]" /></div>;
  }

  return (
      <div className="space-y-8" data-testid="admin-scenarios-page">
        {/* Header */}
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-serif text-4xl text-[#1A1A1A]" data-testid="admin-scenario-count">{scenarios.length}</p>
            <p className="mt-1 text-xs uppercase text-[#8A8D8A]">Practice scenarios</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openCreateDialog}
                className="bg-[#00E085] text-[#00331E] hover:bg-[#00C978] rounded-full px-6"
                data-testid="create-scenario-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Scenario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingScenario ? 'Edit Scenario' : 'Create Scenario'}</DialogTitle>
                <DialogDescription>
                  {editingScenario ? 'Update the scenario details' : 'Add a new practice scenario'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="scenario-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spouse">Spouse</SelectItem>
                        <SelectItem value="kids">Kids</SelectItem>
                        <SelectItem value="parents">Parents</SelectItem>
                        <SelectItem value="retirement">Retirement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Active</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                        data-testid="scenario-active-switch"
                      />
                      <span className="text-sm text-gray-500">
                        {formData.is_active ? 'Visible to users' : 'Hidden'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Budget Disagreement"
                    data-testid="scenario-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the scenario"
                    rows={2}
                    data-testid="scenario-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Goal *</Label>
                  <Textarea
                    value={formData.goal}
                    onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                    placeholder="What the user should accomplish"
                    rows={2}
                    data-testid="scenario-goal-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>System Prompt (AI Role)</Label>
                  <Textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                    placeholder="Instructions for the AI character"
                    rows={3}
                    data-testid="scenario-system-prompt-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Opening Line</Label>
                  <Input
                    value={formData.opening_line}
                    onChange={(e) => setFormData({ ...formData, opening_line: e.target.value })}
                    placeholder="First message from the AI"
                    data-testid="scenario-opening-line-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="scenario-dialog-cancel-button">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-[#00E085] text-[#00331E] hover:bg-[#00C978]"
                  data-testid="scenario-dialog-save-button"
                >
                  {saving ? 'Saving...' : 'Save Scenario'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Scenarios Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="scenarios-table">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#111827]">Scenarios</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scenarios.map((scenario) => (
                  <tr key={scenario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-[#111827]">{scenario.title}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{scenario.description}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        scenario.category === 'spouse' ? 'bg-pink-100 text-pink-700' :
                        scenario.category === 'kids' ? 'bg-blue-100 text-blue-700' :
                        scenario.category === 'parents' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {scenario.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        scenario.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {scenario.is_active ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(scenario)}
                          data-testid={`edit-scenario-${scenario.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(scenario.id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-scenario-${scenario.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}
